export type SlashCommand = {
  name: 'clear' | 'start-adventure' | 'feedback';
};

export function parseSlashCommand(input: string): SlashCommand | null {
  const trimmed = input.trim();
  if (trimmed === '/clear') {
    return { name: 'clear' };
  }
  if (trimmed === '/start-adventure') {
    return { name: 'start-adventure' };
  }
  if (trimmed === '/feedback') {
    return { name: 'feedback' };
  }
  return null;
}

/**
 * Slash command definition for the autocomplete menu
 */
export interface SlashCommandDefinition {
  /** Command name (without the leading /) */
  name: string;
  /** Short description shown in the menu */
  description: string;
  /** If set, replaces input with this text instead of executing */
  fillText?: string;
  /** If true, command takes an argument (shown as placeholder) */
  hasArg?: boolean;
  /** If true, tapping the row fires the command immediately instead of
   *  pre-filling. When `fillText` is also set, the fill text is what gets
   *  sent (e.g. inner-voice prompts). Ignored when `hasArg` is true since
   *  arg-bearing commands need user input. */
  autoExecute?: boolean;
  /** Placeholder text for argument */
  argPlaceholder?: string;
  /** Room types where this command is available (undefined = all rooms) */
  roomTypes?: ('tavian' | 'party' | 'private' | 'aldric' | 'kira' | 'grolt' | 'game' | 'inner')[];
  /** If true, this is a sub-menu option (target, spell, item), not a slash command */
  isMenuOption?: boolean;
  /** Stage label shown above the menu (e.g., "Select target", "Select spell") */
  stageLabel?: string;
  /** Backend intent type hint (e.g., 'attack', 'cast_spell', 'dodge') */
  intentType?: string;
  /** Opaque data carried through selection (e.g., char ID, spell name, item ID) */
  sourceId?: string;
  /** Extra metadata for display (e.g., spell level, health state, distance) */
  meta?: Record<string, unknown>;
  /** Character positions that matched the fuzzy search (for highlighting) */
  matchPositions?: number[];
  /** Keyboard shortcut hint shown on the right (e.g., 'Ctrl+1') */
  shortcutHint?: string;
  /** Emoji to show before the name (e.g., character avatar emoji) */
  emoji?: string;
  /** Portrait/avatar image URL — when set on character pickers, the menu
   *  renders the image instead of the emoji (the emoji is kept as a fallback
   *  for image-load failures). Use the same precedence as the avatar widgets:
   *  `c.avatarUrl ?? c.portraitUrl`. */
  avatarUrl?: string;
  /** Multi-select pickers (e.g. /bless target step): the hook sets this to
   *  true on items the user has currently checked. `SlashCommandMenu`
   *  renders a checkbox/checkmark next to checked items. Default false. */
  isChecked?: boolean;
  /** Marker for the synthetic "Confirm" pseudo-item the hook prepends to a
   *  multi-select stage. The hook recognises this id on selection and
   *  finalises the stage; never used outside the multi-select flow. */
  isMultiSelectConfirm?: boolean;
}

/**
 * All available slash commands
 */
export const SLASH_COMMANDS: SlashCommandDefinition[] = [
  {
    name: 'clear',
    description: 'Clear chat history',
  },
  {
    name: 'feedback',
    emoji: '💬',
    description: 'Send feedback to the team',
  },
  {
    name: 'tavian',
    description: 'Address Tavian directly',
    fillText: 'Tavian: ',
    roomTypes: ['party'],
  },
  {
    name: 'invite',
    description: 'Invite a character to the party',
    hasArg: true,
    argPlaceholder: 'character name',
    roomTypes: ['private', 'tavian'],
  },
  {
    name: 'leave',
    description: 'Leave this chat room',
    roomTypes: ['private'],
    autoExecute: true,
  },
  {
    name: 'form-party',
    description: 'Form a party with chat members',
    hasArg: true,
    argPlaceholder: 'party name',
    roomTypes: ['private'],
  },
  {
    name: 'start-adventure',
    description: 'Start the adventure',
    roomTypes: ['party'],
    autoExecute: true,
  },
  // Rest macros — drop natural-language proposals into party chat so other
  // members + Tavian can respond. Matches mobile's `_kPartySlashCommands`.
  // No autoExecute: lets the player tweak the wording before sending.
  {
    name: 'short-rest',
    emoji: '☕',
    description: 'Propose a short rest',
    fillText: "Let's take a short rest",
    roomTypes: ['party'],
  },
  {
    name: 'long-rest',
    emoji: '💤',
    description: 'Propose a long rest',
    fillText: "Let's take a long rest",
    roomTypes: ['party'],
  },
  // Tavian-only shortcuts. Each rewrites to the natural-language phrasing
  // Tavian's prompt is trained on (see ChatPanel.tsx Tavian branch) so the
  // existing tool-call dispatch handles the action. Visibility is filtered
  // by lobby state in `availableCommands` (no char → /create-char only,
  // etc.) — see getCommandsForRoom + the useMemo in ChatPanel.tsx.
  {
    name: 'create-char',
    description: 'Start creating a new character',
    roomTypes: ['tavian'],
    autoExecute: true,
  },
  {
    name: 'create-party',
    description: 'Form a new party',
    roomTypes: ['tavian'],
    autoExecute: true,
  },
  {
    name: 'select-adventure',
    description: 'Pick an adventure to play',
    roomTypes: ['tavian'],
    autoExecute: true,
  },
  {
    name: 'duel',
    description: 'Challenge to a practice duel',
    roomTypes: ['private', 'tavian'],
    autoExecute: true,
  },
  {
    name: 'talk-to',
    description: 'Open a 1:1 chat with another character',
    hasArg: true,
    argPlaceholder: 'character name',
    roomTypes: ['tavian'],
  },
  {
    name: 'aldric',
    description: 'Go talk to Aldric',
    roomTypes: ['tavian'],
    autoExecute: true,
  },
  {
    name: 'kira',
    description: 'Go talk to Master Kira',
    roomTypes: ['tavian'],
    autoExecute: true,
  },
  {
    name: 'grolt',
    description: "Visit Grolt's shop",
    roomTypes: ['tavian'],
    autoExecute: true,
  },
  {
    name: 'help',
    description: 'What can I do here?',
    roomTypes: ['tavian'],
    autoExecute: true,
  },
  {
    name: 'release-character',
    description: 'Release your current character',
    roomTypes: ['tavian'],
    autoExecute: true,
  },
  {
    name: 'disband-party',
    description: 'Disband your party',
    roomTypes: ['tavian'],
    autoExecute: true,
  },
  {
    name: 'level-up',
    description: 'Level up your character',
    roomTypes: ['tavian'],
    autoExecute: true,
  },
  {
    name: 'who-is-here',
    description: "See who's at camp",
    roomTypes: ['tavian'],
    autoExecute: true,
  },
  {
    name: 'rename-party',
    description: 'Rename your party',
    roomTypes: ['tavian'],
    autoExecute: true,
  },
  // Inner voice commands
  {
    name: 'portrait',
    emoji: '🖼️',
    description: 'New character portrait',
    fillText: 'Generate a new portrait for me',
    roomTypes: ['inner'],
    autoExecute: true,
  },
  {
    name: 'avatar',
    emoji: '👤',
    description: 'New avatar image',
    fillText: 'Generate a new avatar for me',
    roomTypes: ['inner'],
    autoExecute: true,
  },
  {
    name: 'equip',
    emoji: '⚔️',
    description: 'Equip an item from your inventory',
    // hasArg + no autoExecute → tapping pre-fills "/equip " so the
    // ChatPanel picker (apps/web/src/components/panels/ChatPanel.tsx)
    // can swap the menu to show equipable inventory items. Surfaced in
    // both Tavian's chat and inner-voice — the rewriter ships the same
    // "Equip my <item>" phrase either way.
    hasArg: true,
    argPlaceholder: 'item name',
    roomTypes: ['inner', 'tavian'],
  },
  {
    name: 'prepare',
    emoji: '📖',
    description: 'Prepare spells',
    fillText: 'Help me choose which spells to prepare. ',
    roomTypes: ['inner'],
    autoExecute: true,
  },
  {
    name: 'unequip',
    emoji: '🎒',
    description: 'Unequip an item',
    fillText: 'I want to unequip ',
    roomTypes: ['inner'],
    autoExecute: true,
  },
  {
    name: 'review',
    emoji: '📊',
    description: 'Review last session',
    fillText: "Let's discuss my performance in our last adventure. ",
    roomTypes: ['inner'],
    autoExecute: true,
  },
  {
    name: 'improve',
    emoji: '💪',
    description: 'How to grow stronger',
    fillText: 'What should I focus on to become more powerful? ',
    roomTypes: ['inner'],
    autoExecute: true,
  },
  {
    name: 'tactics',
    emoji: '🎯',
    description: 'Combat tactics advice',
    fillText: 'What combat tactics work best for my build? ',
    roomTypes: ['inner'],
    autoExecute: true,
  },
  {
    name: 'backstory',
    emoji: '📜',
    description: 'Explore your backstory',
    fillText: 'Tell me about our past — where did we come from? ',
    roomTypes: ['inner'],
    autoExecute: true,
  },
  {
    name: 'bonds',
    emoji: '🤝',
    description: 'Party relationships',
    fillText: 'How do I feel about my party members? ',
    roomTypes: ['inner'],
    autoExecute: true,
  },
  {
    name: 'goals',
    emoji: '⭐',
    description: 'Character goals & ideals',
    fillText: 'What are my goals and what drives me? ',
    roomTypes: ['inner'],
    autoExecute: true,
  },
  {
    name: 'inventory',
    emoji: '🎒',
    description: 'Review your inventory',
    fillText: "What's in my inventory and what should I use? ",
    roomTypes: ['inner'],
    autoExecute: true,
  },
  // Grolt (merchant) shortcuts. Grolt's agent already handles the
  // natural-language equivalents — these just give the player a
  // tap-to-ask entry point.
  {
    name: 'inventory',
    emoji: '📦',
    description: 'See what Grolt has for sale',
    fillText: 'What have you got?',
    roomTypes: ['grolt'],
    autoExecute: true,
  },
  {
    name: 'get-offer',
    emoji: '💰',
    description: 'get offer for your items',
    // Asks Grolt to appraise the player's entire inventory in one pass —
    // sibling to `/sell <item>` for one-shot per-item quotes. autoExecute
    // mirrors `/inventory`: tap fires the natural-language ask immediately.
    fillText: 'Check my inventory, how much for each item?',
    roomTypes: ['grolt'],
    autoExecute: true,
  },
  {
    name: 'sell',
    emoji: '💰',
    description: 'Sell an item to Grolt',
    // hasArg + no autoExecute → tapping pre-fills "/sell " so the
    // ChatPanel picker can swap to a list of inventory items. The
    // per-item row's fillText is "How much will you give me for X?"
    // (see ChatPanel.tsx Grolt picker branch).
    hasArg: true,
    argPlaceholder: 'item name',
    roomTypes: ['grolt'],
  },
];

/**
 * Get available commands for a specific room type
 */
export function getCommandsForRoom(roomType: string | undefined): SlashCommandDefinition[] {
  return SLASH_COMMANDS.filter((cmd) => {
    if (!cmd.roomTypes) return true; // Available in all rooms
    return cmd.roomTypes.includes(
      roomType as SlashCommandDefinition['roomTypes'] extends (infer T)[] ? T : never
    );
  });
}

/**
 * Filter commands by search query (what user typed after /)
 * Uses prefix matching for lobby commands (simple, few commands).
 */
export function filterCommands(
  commands: SlashCommandDefinition[],
  query: string
): SlashCommandDefinition[] {
  const lowerQuery = query.toLowerCase();
  return commands.filter((cmd) => cmd.name.toLowerCase().startsWith(lowerQuery));
}

// =============================================================================
// Fuzzy Search (for game command menus with many items)
// =============================================================================

export interface FuzzyResult {
  item: SlashCommandDefinition;
  score: number;
  positions: number[];
}

/**
 * Fuzzy match: characters must appear in order but not necessarily contiguous.
 * Consecutive matches and prefix matches score higher.
 * Returns null if no match, or { score, positions } if matched.
 */
export function fuzzyMatch(
  text: string,
  query: string
): { score: number; positions: number[] } | null {
  if (!query) return { score: 0, positions: [] };
  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();

  const positions: number[] = [];
  let textIdx = 0;

  for (let qi = 0; qi < lowerQuery.length; qi++) {
    const found = lowerText.indexOf(lowerQuery[qi], textIdx);
    if (found === -1) return null;
    positions.push(found);
    textIdx = found + 1;
  }

  // Score: higher is better
  let score = 100;
  // Bonus for prefix match (query matches start of text)
  if (positions[0] === 0) score += 50;
  // Bonus for consecutive character matches
  let consecutive = 0;
  for (let i = 1; i < positions.length; i++) {
    if (positions[i] === positions[i - 1] + 1) consecutive++;
  }
  score += consecutive * 10;
  // Bonus for exact match
  if (lowerText === lowerQuery) score += 200;
  // Bonus for shorter names (more specific matches)
  score -= text.length;

  return { score, positions };
}

/**
 * Fuzzy filter + rank commands. Returns items with matchPositions set.
 */
export function fuzzyFilterCommands(
  commands: SlashCommandDefinition[],
  query: string
): SlashCommandDefinition[] {
  if (!query) return commands;

  const results: FuzzyResult[] = [];
  for (const cmd of commands) {
    const match = fuzzyMatch(cmd.name, query);
    if (match) {
      results.push({ item: cmd, score: match.score, positions: match.positions });
    }
  }

  // Sort by score descending
  results.sort((a, b) => b.score - a.score);

  return results.map((r) => ({
    ...r.item,
    matchPositions: r.positions,
  }));
}

export type InputState = {
  message: string;
  history: string[];
  historyIndex: number;
  savedInput: string;
};

export const defaultInputState: InputState = {
  message: '',
  history: [],
  historyIndex: -1,
  savedInput: '',
};

export function getInputState(states: Record<string, InputState>, roomId: string): InputState {
  return states[roomId] ?? defaultInputState;
}

/** A row in the `/invite` character picker. */
export type InvitePickerRow = {
  id: string;
  name: string;
  species?: string;
  class?: string;
  level?: number;
  avatarEmoji?: string;
  /** The friend is not in the camp right now. Rendered as "· away" so nobody
   *  reads the silence after an invite as a failure. */
  away?: boolean;
};

/** Minimal shape of a camp-present character the picker needs. */
type PresentCharacter = {
  id: string;
  type: string;
  name: string;
  species?: string;
  class?: string;
  level?: number;
  avatarEmoji?: string;
};

/** Minimal shape of a friend-list entry the picker needs. */
type FriendEntry = {
  charId?: string;
  charName?: string;
  displayName: string;
  charSpecies?: string;
  charClass?: string;
  charLevel?: number;
  avatarEmoji?: string;
  inParty: boolean;
  online: boolean;
};

/**
 * Build the `/invite` picker rows: everyone you could invite to your party.
 *
 * Two sources, because neither alone is the answer. `present` covers the
 * players standing in the camp right now. `friends` covers everyone else you
 * know, online or not, since an invite to an absent friend now waits for them
 * rather than being refused, and the camp pool by definition cannot hold an
 * offline player's hero.
 *
 * Rules, all of them load-bearing:
 * - NPCs are never listed. A camp NPC named here only walks into the
 *   in-character refusal.
 * - You are never listed.
 * - A friend already standing in the camp is listed once, from `present`, so
 *   the two sources cannot produce a duplicate row.
 * - A friend whose character already belongs to a party is dropped: they
 *   cannot join a second one.
 * - A friend with no character has nothing to invite.
 */
export function buildInvitePickerRows(
  present: PresentCharacter[],
  friends: FriendEntry[],
  selfCharacterId: string | null | undefined
): InvitePickerRow[] {
  const presentRows = present.filter((c) => c.type === 'pc' && c.id !== selfCharacterId);
  const presentIds = new Set(presentRows.map((c) => c.id));
  const awayFriends = friends
    .filter(
      (f) => !!f.charId && !f.inParty && f.charId !== selfCharacterId && !presentIds.has(f.charId)
    )
    .map<InvitePickerRow>((f) => ({
      id: f.charId as string,
      name: f.charName ?? f.displayName,
      species: f.charSpecies,
      class: f.charClass,
      level: f.charLevel,
      avatarEmoji: f.avatarEmoji,
      away: !f.online,
    }));
  return [...presentRows, ...awayFriends];
}
